import React from 'react';
import type { PaymentFormErrors } from '../types/payment-form.types';

declare global {
  interface Window {
    Pagou?: any;
    __pagouSubmitToken?: () => Promise<string>;
    __pagouResetToken?: () => void;
    __pagouCardReady?: boolean;
  }
}

interface PagouPaymentElementFormProps {
  errors: PaymentFormErrors;
  pagouPublicKey: string;
  pagouProduction?: boolean;
  /**
   * Quando true, há upsell one-click: o submit usa `mode: "upsell"` para gerar um token
   * REUTILIZÁVEL (`pgpm_`), reusado na cobrança do upsell. Sem upsell, mantém `pgct_` (uso único).
   */
  hasOneClickUpsell?: boolean;
  /** Reporta o token reutilizável `pgpm_` capturado ao parent (retido para reuso no upsell). */
  onReusableToken?: (token: string) => void;
}

const PAGOU_SCRIPT_SRC = 'https://js.pagou.ai/payments/v3.js';
/** Tempo máximo aguardando o evento `ready` do iframe antes de considerar falha de carga. */
const READY_TIMEOUT_MS = 8000;
/** Intervalo entre remounts quando o iframe falha (ERR_CONNECTION_RESET, etc.). */
const RETRY_INTERVAL_MS = 2000;

const loadPagouScript = (): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Ambiente inválido para carregar o SDK da Pagou.'));
  }
  if (window.Pagou) return Promise.resolve();

  const existing = document.querySelector(`script[src="${PAGOU_SCRIPT_SRC}"]`) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Falha ao carregar script da Pagou.')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PAGOU_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Falha ao carregar script da Pagou.'));
    document.head.appendChild(script);
  });
};

type PagouLoadStatus = 'loading' | 'ready' | 'error';

const PagouPaymentElementForm: React.FC<PagouPaymentElementFormProps> = ({
  errors,
  pagouPublicKey,
  pagouProduction: _pagouProduction,
  hasOneClickUpsell,
  onReusableToken,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const elementsRef = React.useRef<any>(null);
  const cardReadyRef = React.useRef(false);
  const [token, setToken] = React.useState('');
  const [loadStatus, setLoadStatus] = React.useState<PagouLoadStatus>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [remountKey, setRemountKey] = React.useState(0);
  const [retryAttempt, setRetryAttempt] = React.useState(0);

  const cleanKey = (pagouPublicKey || '').trim();

  const markReady = React.useCallback(() => {
    cardReadyRef.current = true;
    setLoadStatus('ready');
    setErrorMessage(null);
    setRetryAttempt(0);
    if (typeof window !== 'undefined') {
      window.__pagouCardReady = true;
    }
  }, []);

  const markLoadError = React.useCallback((message: string) => {
    cardReadyRef.current = false;
    setLoadStatus('error');
    setErrorMessage(message);
    if (typeof window !== 'undefined') {
      window.__pagouCardReady = false;
    }
  }, []);

  const hasOneClickUpsellRef = React.useRef(!!hasOneClickUpsell);
  const onReusableTokenRef = React.useRef(onReusableToken);
  hasOneClickUpsellRef.current = !!hasOneClickUpsell;
  onReusableTokenRef.current = onReusableToken;

  const submitToken = React.useCallback(async (): Promise<string> => {
    // Tokens da Pagou são de uso único: SEMPRE geramos um novo via elements.submit.
    // Nunca reutilizar token de state — causa token_consumed na 2ª tentativa.
    const elements = elementsRef.current;
    if (!elements) {
      throw new Error('Payment Element da Pagou não inicializado.');
    }

    if (!cardReadyRef.current) {
      throw new Error('Campo do cartão ainda não está pronto. Aguarde o carregamento.');
    }

    setErrorMessage(null);
    let capturedToken = '';
    // Com upsell, o submit pede um token REUTILIZÁVEL (pgpm_); sem upsell, uso único (pgct_).
    const submitOptions: { mode?: 'upsell'; createTransaction: (tokenData: any) => Promise<unknown> } = {
      ...(hasOneClickUpsellRef.current ? { mode: 'upsell' as const } : {}),
      createTransaction: async (tokenData: any) => {
        const generatedToken = String(tokenData?.token || '').trim();
        const isReusable = generatedToken.startsWith('pgpm_');
        const isSingleUse = generatedToken.startsWith('pgct_');
        if (!isReusable && !isSingleUse) {
          throw new Error('Token Pagou inválido. Gere novamente o cartão.');
        }
        capturedToken = generatedToken;
        setToken(generatedToken);
        if (isReusable) {
          onReusableTokenRef.current?.(generatedToken);
        }

        // Pagou docs: tokenização no SDK; POST /v2/transactions só no backend (placeOrder).
        // Não chamar checkout aqui — evita Failed to fetch e dupla cobrança no 1º clique.
        return { status: 'tokenized' };
      },
    };
    const submitResult = await elements.submit(submitOptions);

    if (submitResult?.status === 'error') {
      const sdkError = submitResult?.error;
      const sdkErrorMessage =
        typeof sdkError === 'string'
          ? sdkError
          : String(sdkError?.message || submitResult?.message || 'Falha ao validar cartão na Pagou.');
      const sdkErrorCode = String(
        typeof sdkError === 'object' && sdkError ? sdkError?.code || submitResult?.code || 'unknown' : submitResult?.code || 'unknown',
      );
      const sdkErrorType = String(
        typeof sdkError === 'object' && sdkError ? sdkError?.type || submitResult?.type || 'unknown' : submitResult?.type || 'unknown',
      );

      setErrorMessage(sdkErrorMessage);
      throw new Error(`Falha ao validar cartão na Pagou. code=${sdkErrorCode} type=${sdkErrorType} message=${sdkErrorMessage}`);
    }

    if (capturedToken.startsWith('pgct_') || capturedToken.startsWith('pgpm_')) {
      return capturedToken;
    }

    throw new Error('Não foi possível obter token da Pagou.');
  }, []);

  // Item 2: rede de segurança — limpa o token exibido após falha de pagamento.
  // Sem cache de reuso (item 1), cada submitToken já regenera; este reset zera
  // o input oculto/UI e o ref de resposta pré-criada via useCheckoutPayment.
  const resetToken = React.useCallback(() => {
    setToken('');
    setErrorMessage(null);
  }, []);

  const submitTokenRef = React.useRef(submitToken);
  const resetTokenRef = React.useRef(resetToken);
  submitTokenRef.current = submitToken;
  resetTokenRef.current = resetToken;

  // Bridge estável: não recria/apaga window.__pagouSubmitToken a cada cardReady/upsell.
  // O bug "Campo do cartão Pagou ainda não carregou" na linha else (sem submitToken)
  // vinha do cleanup delete durante re-render do useCallback.
  React.useEffect(() => {
    if (!cleanKey) {
      window.__pagouCardReady = false;
      return;
    }
    window.__pagouSubmitToken = () => submitTokenRef.current();
    window.__pagouResetToken = () => resetTokenRef.current();
    return () => {
      delete window.__pagouSubmitToken;
      delete window.__pagouResetToken;
      window.__pagouCardReady = false;
    };
  }, [cleanKey]);

  React.useEffect(() => {
    let isMounted = true;
    let mountedCard: any = null;
    let readyTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let loadFailed = false;

    const scheduleRetry = (reason: string) => {
      if (!isMounted || loadFailed) return;
      loadFailed = true;
      markLoadError(reason);
      setRetryAttempt((n) => n + 1);
      retryTimeoutId = setTimeout(() => {
        if (!isMounted) return;
        setRemountKey((k) => k + 1);
      }, RETRY_INTERVAL_MS);
    };

    const setup = async () => {
      try {
        if (!cleanKey) {
          markLoadError('Public Key Pagou ausente. Configure a chave pública no admin da loja.');
          return;
        }

        // TeamDrop opera só em produção Pagou (api.pagou.ai).
        cardReadyRef.current = false;
        setLoadStatus('loading');
        setErrorMessage(null);
        if (typeof window !== 'undefined') {
          window.__pagouCardReady = false;
        }
        await loadPagouScript();
        if (!isMounted || !window.Pagou || !containerRef.current) return;

        // Força production (evita sticky sandbox de sessão anterior no mesmo tab).
        if (typeof window.Pagou.setEnvironment === 'function') {
          window.Pagou.setEnvironment('production');
        }

        const elements = window.Pagou.elements({
          publicKey: cleanKey,
          locale: 'pt-BR',
          origin: window.location.origin,
        });
        elementsRef.current = elements;

        mountedCard = elements.create('card', { theme: 'default' });
        mountedCard.mount(containerRef.current);

        mountedCard.on('ready', () => {
          if (!isMounted || loadFailed) return;
          if (readyTimeoutId) {
            clearTimeout(readyTimeoutId);
            readyTimeoutId = null;
          }
          markReady();
        });

        mountedCard.on('change', (event: any) => {
          if (!isMounted) return;
          // Erros de validação do campo (cartão inválido) — não remonta o iframe.
          if (event?.error?.message) {
            setErrorMessage(String(event.error.message));
            return;
          }
          if (event?.valid) {
            setErrorMessage(null);
          }
        });

        mountedCard.on('error', (event: any) => {
          if (!isMounted) return;
          const msg = String(event?.message || 'Erro ao carregar campo do cartão.');
          // Antes do ready = falha de carga do iframe → retry. Depois = erro de campo.
          if (!cardReadyRef.current) {
            scheduleRetry(msg);
            return;
          }
          setErrorMessage(msg);
        });

        readyTimeoutId = setTimeout(() => {
          if (!isMounted || cardReadyRef.current) return;
          scheduleRetry(
            'Campo do cartão não carregou a tempo (iframe Pagou). Reconectando…',
          );
        }, READY_TIMEOUT_MS);
      } catch (err: any) {
        if (!isMounted) return;
        scheduleRetry(err?.message || 'Erro ao inicializar Pagou Payment Element.');
      }
    };

    setup();
    return () => {
      isMounted = false;
      if (readyTimeoutId) clearTimeout(readyTimeoutId);
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
      setToken('');
      cardReadyRef.current = false;
      if (typeof window !== 'undefined') {
        window.__pagouCardReady = false;
      }
      if (mountedCard && typeof mountedCard.unmount === 'function') {
        mountedCard.unmount();
      }
      elementsRef.current = null;
    };
  }, [cleanKey, remountKey, markReady, markLoadError]);

  return (
    <div className="space-y-0" data-pagou-status={loadStatus} data-pagou-retry={retryAttempt}>
      <div>
        <div>
          <div ref={containerRef} key={remountKey} />
        </div>
        {errors.cardNumber && <p className="text-xs text-red-500 mt-1">{errors.cardNumber}</p>}
      </div>

      {loadStatus === 'loading' && (
        <p className="text-xs text-text-secondary dark:text-gray-400">
          {retryAttempt > 0
            ? `Reconectando checkout seguro da Pagou… (tentativa ${retryAttempt + 1})`
            : 'Carregando checkout seguro da Pagou...'}
        </p>
      )}

      {loadStatus === 'error' && (
        <p className="text-xs text-red-500" role="alert">
          {errorMessage || 'Falha ao carregar o cartão.'}
          {' '}
          Nova tentativa em {RETRY_INTERVAL_MS / 1000}s
          {retryAttempt > 0 ? ` · tentativa ${retryAttempt}` : ''}.
        </p>
      )}

      {loadStatus === 'ready' && errorMessage && (
        <p className="text-xs text-red-500">{errorMessage}</p>
      )}

      <input type="hidden" name="cardNumber" value={token} />
      <input type="hidden" name="expirationDate" value="" />
      <input type="hidden" name="cvv" value="" />
    </div>
  );
};

export default PagouPaymentElementForm;
